import React, { useCallback, useContext, useMemo } from 'react';
import BayColorPicker, {
	type DynamicCopy,
	ADMIN_DYNAMIC_COPY,
} from '@bay-ui/BayColorPicker';
import { PresetColorsContext } from '@extensions/AttributePanel/components/provider/PresetColorsProvider';
import { useEditorProps } from '@bay-email/editor';
import { resolveColorTagValue, isMergeTagExpression } from '@bay-email/core';

export interface ColorPickerControlProps {
	onChange?: (val: string) => void;
	value?: string;
	children?: React.ReactNode;
	getPopupContainer?: () => HTMLElement;
	label?: string;
	showInput?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	/**
	 * Enables the Static / Dynamic toggle and the dynamic brand-color panel.
	 * Defaults to true for the attribute drawer; pass false to keep a control static.
	 */
	supportsDynamic?: boolean;
	/** Optional copy overrides for the dynamic panel, passed straight to BayColorPicker. */
	dynamicCopy?: DynamicCopy;
}

export function ColorPickerControl(props: ColorPickerControlProps) {
	const { colors, addCurrentColor } = useContext(PresetColorsContext);
	const { mergeTags, mergeTagGenerate, isGlobalRole } = useEditorProps();

	// Global-admin roles get the admin-authoring copy; everyone else keeps the
	// default. An explicit dynamicCopy prop always wins. The role is threaded in
	// via editor props from the host app's access state — the editor runs against
	// its own redux store (no actingAs slice), so a useSelector here can't see it.
	const resolvedDynamicCopy =
		props.dynamicCopy ?? (isGlobalRole ? ADMIN_DYNAMIC_COPY : undefined);

	const {
		value = '',
		onChange,
		children,
		showInput = !children,
		supportsDynamic = true,
	} = props;

	const presetColors = useMemo(() => {
		const filteredColors = colors.filter(color => color.toLowerCase() !== 'transparent');
		return [...filteredColors, 'Transparent'];
	}, [colors]);

	// Brand colors: stored as a dynamic merge tag, displayed as the resolved hex.
	const brandColors = useMemo(() => {
		const user = (mergeTags?.user || {}) as Record<string, string>;
		const gen = (path: string) =>
			mergeTagGenerate ? mergeTagGenerate(path) : `{{${path}}}`;
		const out: Array<{ label: string; color: string; value: string }> = [];
		if (user.brandPrimaryColor) {
			out.push({
				label: 'Primary',
				color: user.brandPrimaryColor,
				value: gen('user.brandPrimaryColor'),
			});
		}
		if (user.brandSecondaryColor) {
			out.push({
				label: 'Secondary',
				color: user.brandSecondaryColor,
				value: gen('user.brandSecondaryColor'),
			});
		}
		return out;
	}, [mergeTags, mergeTagGenerate]);

	// A dynamic brand color is stored as the bare merge tag (e.g.
	// "{{user.brandPrimaryColor}}"). The picker detects dynamic by matching the value
	// against brandColors, so pass brand tags through raw. Other merge tags resolve to
	// their hex; literal colors pass through unchanged.
	const isBrandTag = useMemo(
		() => brandColors.some(b => b.value === value),
		[brandColors, value],
	);
	const pickerValue = useMemo(() => {
		if (isBrandTag) return value;
		if (isMergeTagExpression(value)) {
			return resolveColorTagValue(value, mergeTags) || value;
		}
		return value;
	}, [isBrandTag, value, mergeTags]);

	// Live change while dragging in the popover (static path only — dynamic commits whole).
	const handleColorChange = useCallback(
		(nextColor: string) => {
			const normalizedValue =
				nextColor.toLowerCase() === 'transparent' ? 'Transparent' : nextColor;
			onChange?.(normalizedValue);
		},
		[onChange],
	);

	// Commit: record literal colors as recent; never store a merge tag as a recent color.
	const handleColorCommit = useCallback(
		(nextColor: string) => {
			const normalizedValue =
				nextColor.toLowerCase() === 'transparent' ? 'Transparent' : nextColor;
			onChange?.(normalizedValue);
			if (!isMergeTagExpression(normalizedValue)) {
				addCurrentColor(normalizedValue);
			}
		},
		[onChange, addCurrentColor],
	);

	return (
		<BayColorPicker
			value={pickerValue}
			onChange={handleColorChange}
			onCommit={handleColorCommit}
			onOpenChange={props.onOpenChange}
			presetColors={presetColors}
			brandColors={brandColors}
			showBrandColors
			supportsDynamic={supportsDynamic && brandColors.length > 0}
			dynamicCopy={resolvedDynamicCopy}
			showInput={showInput}
			getPopupContainer={props.getPopupContainer}
		>
			{children}
		</BayColorPicker>
	);
}
